import {
  CERTIFICATION_PROJECT_STATUS,
  NATURE_DECISION_CERTIFICATION_PROJECT,
  STATUS_CAN_REGISTER_AGAIN,
} from "@/models/enums/certificationProjectStatus";
import { CYCLE_TYPE } from "@/models/enums/cycleType";
import { Cycle, CertipersUser } from "@/models";
import { useI18n } from "vue-i18n";
import { calculateEndDate } from "@/utils/convertRelativeDeltaField";
import { useRepo, Model } from "pinia-orm";

export default class CertificationProject extends Model {
  static entity = "certificationProjects";
  static primaryKey = "uuid";

  static fields() {
    return {
      uuid: this.uid(""),
      createdAt: this.string(""),
      updatedAt: this.string(""),
      certifiedDate: this.string(""),
      oldCertifiedDate: this.string(""),
      manualCertifiedDate: this.string(""),
      status: this.string(CERTIFICATION_PROJECT_STATUS.PENDING),
      motif: this.attr(""),
      notes: this.attr(""),
      mention: this.attr(""),
      mentionReductionDate: this.string(""),
      mentionExtensionDate: this.string(""),
      hasMentionChanged: this.boolean(false),
      sanctionDate: this.string(""),
      effectiveTransferDate: this.string(""),
      dueDate: this.string(""),
      cycleEndDate: this.string(""),
      examinations: this.attr([]),

      // Foreign keys
      certipersUserUuid: this.string(""),
      certipersUserData: this.belongsTo(CertipersUser, "certipersUserUuid"),
      cycleUuid: this.string(""),
      cycleData: this.belongsTo(Cycle, "cycleUuid"),
      extensionOfUuid: this.string(""),
      extensionOfData: this.belongsTo(CertificationProject, "extensionOfUuid"),

      // Other
      isDisplayed: this.boolean(false),
    };
  }

  get certificationEndDate() {
    if (this.cycleData.maxEndDate) {
      return this.cycleData.maxEndDate;
    }
    if (this.manualCertifiedDate) {
      return calculateEndDate(this.manualCertifiedDate, this.cycleData.duration);
    }
    if (this.oldCertifiedDate) {
      return calculateEndDate(this.oldCertifiedDate, this.cycleData.duration);
    }
    if (this.certifiedDate) {
      return calculateEndDate(this.certifiedDate, this.cycleData.duration);
    }
    return null;
  }

  get decisionNature() {
    if (this.status === CERTIFICATION_PROJECT_STATUS.KO) {
      return NATURE_DECISION_CERTIFICATION_PROJECT.NOT_VALIDATED;
    }
    if (
      this.status === CERTIFICATION_PROJECT_STATUS.PENDING ||
      this.status === CERTIFICATION_PROJECT_STATUS.PENDING_PAYMENT ||
      this.status === CERTIFICATION_PROJECT_STATUS.WIP
    ) {
      return NATURE_DECISION_CERTIFICATION_PROJECT.WIP;
    }
    if (this.status === CERTIFICATION_PROJECT_STATUS.UNCERTIFIED) {
      if (this.cycleData.cycleType === CYCLE_TYPE.INITIALIZATION) {
        return NATURE_DECISION_CERTIFICATION_PROJECT.UNCERTIFIED;
      } else {
        return NATURE_DECISION_CERTIFICATION_PROJECT.UNRECERTIFIED;
      }
    }
    // certified, suspended, revoked_monitoring, revoked_end_cycle, stopped statuses
    if (this.cycleData.cycleType === CYCLE_TYPE.INITIALIZATION) {
      return NATURE_DECISION_CERTIFICATION_PROJECT.CERTIFIED;
    } else {
      return NATURE_DECISION_CERTIFICATION_PROJECT.RECERTIFIED;
    }
  }

  get domainNameWithMention() {
    const { t } = useI18n();
    const CertificationProjectRepo = useRepo(CertificationProject);

    const domainName = CertificationProjectRepo.where("uuid", this.uuid)
      .with("cycleData", (query) =>
        query.with("domainVersionData", (query) => query.with("domainData")),
      )
      .first().cycleData.domainVersionData.domainData.name;
    return this.mention
      ? `${domainName} ${t(this.mention.toLowerCase())} ${t("mention")}`
      : domainName;
  }

  static queryCandidateAlreadyRegistered(cycleUuid) {
    const CertificationProjectRepo = useRepo(CertificationProject);
    // INFO - AG 28/07/2023 - If the candidate has already a certifciation project on this cycle of certification
    // He can not register twice
    return (
      CertificationProjectRepo.where("cycleUuid", cycleUuid)
        .where("status", (value) => !STATUS_CAN_REGISTER_AGAIN.includes(value))
        .get().length > 0
    );
  }
}
